import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  VStack,
  Heading,
  Text,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';

const SimpleSignupForm = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    athlete_first_name: '',
    athlete_last_name: '',
    team_name: '',
    terms_accepted: false,
  });

  // ------------------------------------------------------------------
  // Load event details
  // ------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('events')
          .select('id,name,description,signup_message')
          .eq('id', eventId)
          .single();

        if (error) throw error;
        setEvent(data);
      } catch (err) {
        console.error(err);
        toast({ title: 'Event not found', status: 'error' });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) load();
  }, [eventId]);

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast({ title: 'Please accept the terms of service', status: 'warning' });
      return;
    }
    try {
      const answers = {
        athlete_first_name: form.athlete_first_name,
        athlete_last_name: form.athlete_last_name,
        team_name: form.team_name,
        terms_accepted: form.terms_accepted,
      };
      const payload = {
        email: form.email,
        firstName: form.first_name,
        lastName: form.last_name,
        phone: form.phone,
        eventId,
        answers,
        eventName: event.name,
        dashboardUrl: `${window.location.origin}/dashboard`,
        message: event.signup_message || '',
      };

      const { error } = await supabase.functions.invoke('event-signup', {
        body: payload,
      });
      if (error) throw error;

      toast({
        title: 'Registration received',
        description: 'Check your inbox for confirmation.',
        status: 'success',
      });

      navigate('/signup-success');
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: err.message, status: 'error' });
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <Box py={20} textAlign="center">
        <Spinner />
      </Box>
    );
  }

  return (
    <Container maxW="md" py={10}>
      <VStack spacing={6} as="form" onSubmit={handleSubmit} align="stretch">
        <Heading size="lg" textAlign="center">
          {event.name}
        </Heading>
        <Text textAlign="center" color="gray.600">
          {event.description}
        </Text>

        <FormControl isRequired>
          <FormLabel>First name</FormLabel>
          <Input name="first_name" value={form.first_name} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Last name</FormLabel>
          <Input name="last_name" value={form.last_name} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input type="email" name="email" value={form.email} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Phone</FormLabel>
          <Input name="phone" value={form.phone} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Athlete first name</FormLabel>
          <Input name="athlete_first_name" value={form.athlete_first_name} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Athlete last name</FormLabel>
          <Input name="athlete_last_name" value={form.athlete_last_name} onChange={handleChange} />
        </FormControl>
        <FormControl>
          <FormLabel>Team name (optional)</FormLabel>
          <Input name="team_name" value={form.team_name} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <Checkbox name="terms_accepted" isChecked={form.terms_accepted} onChange={handleChange}>
            I accept the Terms of Service and Privacy Policy
          </Checkbox>
        </FormControl>

        <Button type="submit" colorScheme="blue">
          Complete Registration
        </Button>
      </VStack>
    </Container>
  );
};

export default SimpleSignupForm; 